import api from './api';
import { User } from '../types';

export interface UserWithGrower extends User {
  grower_names: string[];
  last_login_at: string | null;
  is_logged_in: boolean;
}

export interface CreateUserData {
  email: string;
  password: string;
  full_name?: string;
  is_admin?: boolean;
  is_admin_grower?: boolean;
  grower_ids?: number[];
}

export interface UpdateUserData {
  email?: string;
  password?: string;
  full_name?: string;
  is_active?: boolean;
  is_admin?: boolean;
  is_admin_grower?: boolean;
  grower_ids?: number[] | null;
  language?: string;
}

export interface SavedFilter {
  name: string;
  searchTerm: string;
  filterApplication: string;
  filterGrower: string;
  filterStatus: string;
  filterTTIStatus: string;
  filterRegion: string;
  filterSensorType: string;
  filterGateway?: string;
}

export interface SavedFiltersResponse {
  saved_filters: SavedFilter[];
  last_used_filter: string | null;
}

export interface DashboardSavedFilter {
  name: string;
  searchTerm: string;
  filterApplication: string;
  filterGrower: string;
  filterStatus: string;
  filterSensorType: string;
  pinnedSensorIds: number[];
}

export interface DashboardSavedFiltersResponse {
  saved_filters: DashboardSavedFilter[];
  last_used_filter: string | null;
}

export const userService = {
  async getUsers(): Promise<UserWithGrower[]> {
    const response = await api.get<UserWithGrower[]>('/api/users/');
    return response.data;
  },

  async getUser(id: number): Promise<UserWithGrower> {
    const response = await api.get<UserWithGrower>(`/api/users/${id}`);
    return response.data;
  },

  async createUser(data: CreateUserData): Promise<User> {
    const response = await api.post<User>('/api/users/', data);
    return response.data;
  },

  async updateUser(id: number, data: UpdateUserData): Promise<User> {
    const response = await api.put<User>(`/api/users/${id}`, data);
    return response.data;
  },

  async deleteUser(id: number): Promise<void> {
    await api.delete(`/api/users/${id}`);
  },

  async sendPasswordReset(id: number): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(`/api/users/${id}/send-password-reset`);
    return response.data;
  },

  async getSavedFilters(): Promise<SavedFiltersResponse> {
    const response = await api.get<SavedFiltersResponse>('/api/users/me/saved-filters');
    return response.data;
  },

  async upsertSavedFilter(filter: SavedFilter): Promise<SavedFiltersResponse> {
    const response = await api.put<SavedFiltersResponse>('/api/users/me/saved-filters', filter);
    return response.data;
  },

  async deleteSavedFilter(name: string): Promise<SavedFiltersResponse> {
    const response = await api.delete<SavedFiltersResponse>(`/api/users/me/saved-filters/${encodeURIComponent(name)}`);
    return response.data;
  },

  async setLastUsedFilter(name: string | null): Promise<void> {
    await api.put('/api/users/me/saved-filters-last-used', { name });
  },

  async getDashboardSavedFilters(): Promise<DashboardSavedFiltersResponse> {
    const response = await api.get<DashboardSavedFiltersResponse>('/api/users/me/dashboard-saved-filters');
    return response.data;
  },

  async upsertDashboardSavedFilter(filter: DashboardSavedFilter): Promise<DashboardSavedFiltersResponse> {
    const response = await api.put<DashboardSavedFiltersResponse>('/api/users/me/dashboard-saved-filters', filter);
    return response.data;
  },

  async deleteDashboardSavedFilter(name: string): Promise<DashboardSavedFiltersResponse> {
    const response = await api.delete<DashboardSavedFiltersResponse>(`/api/users/me/dashboard-saved-filters/${encodeURIComponent(name)}`);
    return response.data;
  },

  async setLastUsedDashboardFilter(name: string | null): Promise<void> {
    await api.put('/api/users/me/dashboard-saved-filters-last-used', { name });
  },
};
